/** Reading a 409 `UIVisibleError` body off a failed location write, shared by the page's conflict readers. */

const HTTP_CONFLICT = 409;

export type UnknownRecord = Record<string, unknown>;

export const isRecord = (value: unknown): value is UnknownRecord => typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * The error itself, then the error it wraps: the save pipelines wrap the Axios error in a generic
 * `Error(..., { cause })` (the Department Configuration save does), so the response can sit one level
 * down, where `parseJobRemovalConflict` reads it too.
 */
const errorAndCause = (error: unknown): unknown[] => (error instanceof Error && error.cause !== undefined ? [error, error.cause] : [error]);

/** `response.data.detail` of an Axios-shaped error, when it is FastAPI's object form (it can also be a plain string). */
const conflictDetail = (error: unknown): UnknownRecord | null => {
	if (!isRecord(error) || !isRecord(error.response) || error.response.status !== HTTP_CONFLICT) return null;
	const { data } = error.response;
	if (!isRecord(data) || !isRecord(data.detail)) return null;
	return data.detail;
};

/** The 409 `detail` object (`{message, details?}`) of the error or the error it wraps; null when neither is a 409 in object form. */
export const conflictDetailOf = (error: unknown): UnknownRecord | null => {
	const found = errorAndCause(error)
		.map(conflictDetail)
		.find((detail): detail is UnknownRecord => detail !== null);
	return found === undefined ? null : found;
};
